/**
 * Write-site hover hints (v0.10 Phase 2 §8) — two augmentations the plain
 * symbol card cannot express because they describe a *choice the author made at
 * the call/selector site*, not a property of the resolved symbol:
 *
 *  HINT A — maturation semantics of a process-step callee (§4.13.2). A bare
 *    `> Payments` matures silently to the module's default interface if it
 *    declares one; a trailing-dot `> Payments.` pins the interface unspecified
 *    (a durable TODO). The card the cursor lands on is the *module*, so neither
 *    future is visible there — this note supplies it.
 *
 *  HINT B — `count ( <edge-pattern> )` counts EDGES (one per deriving step),
 *    not distinct callers — a legal-but-wrong trap. Hovering the `count` head
 *    reports N edges / M distinct sources against the current model and, when
 *    they differ, nudges toward `count (sources of (…))`.
 *
 * Both are pure functions over the workspace's resolved model + derived graph;
 * `hover.ts` composes them into the returned card.
 */

import type { Hover } from "vscode-languageserver";
import {
  buildClosure,
  buildNodeUniverse,
  classifyCallee,
  evalSelector,
  type DependencyGraph,
  type EvalContext,
  type Expr,
  type ExprAggregate,
  type PolicyDecl,
  type PolicyRule,
  type ProcessStep,
  type QueryDecl,
  type Selector,
  type SimpleStep,
  type SourceSpan,
  type StyleMember,
  type ViewClause,
} from "@archlang/engine";
import type { Workspace } from "../workspace/workspace.js";
import { toLspRange } from "../lsp-types.js";

// =============================================================================
// HINT A — `> X` vs `> X.` maturation (§4.13.2)
// =============================================================================

/**
 * If `offset` lands inside a process-step callee that targets a MODULE (a bare
 * `> X` or a trailing-dot `> X.`), return the one-sentence maturation note plus
 * the callee span. Returns `null` for the full form (`> X.iface`, a declared
 * interface leaf) and for non-step positions.
 */
export function maturationHint(
  workspace: Workspace,
  uri: string,
  offset: number,
): { note: string; span: SourceSpan } | null {
  const model = workspace.model();
  const steps: SimpleStep[] = [];
  for (const p of model.processes) collectSimpleSteps(p.steps, steps);
  for (const s of model.subprocesses) collectSimpleSteps(s.steps, steps);

  const step = steps.find((s) => s.toSource && spanCovers(s.toSource, uri, offset));
  if (!step) return null;

  const to = step.to;
  if (to.length === 0) return null;
  const moduleName = to[to.length - 1]!;
  const trailingDot = step.toUnspecified === true;

  if (trailingDot) {
    return {
      span: step.toSource!,
      note:
        `*write-site (§4.13.2):* Trailing dot — explicitly unspecified interface; ` +
        `stays a TODO even if \`${moduleName}\` later declares a default (the dot pins it).`,
    };
  }

  const graph = workspace.dependencyGraph();
  const cls = classifyCallee(step, calleeIndex(graph));
  // Full form: the terminal segment IS a declared/deduced interface leaf (no
  // module target). That is a mature, ordinary call — no write-site note.
  const isFullForm = cls.kind === "interface" && cls.interfaceSegs.length === to.length;
  if (isFullForm) return null;

  // Otherwise the callee targets a module. Matured-via-default when the classifier
  // appended a real default leaf; an unspecified-interface TODO otherwise.
  const current =
    cls.kind === "interface"
      ? "currently mature via its default interface"
      : "currently an unspecified-interface TODO";
  return {
    span: step.toSource!,
    note:
      `*write-site (§4.13.2):* Bare module ref — resolves to \`${moduleName}\`'s default ` +
      `interface if it declares one (matures silently); ${current}.`,
  };
}

/** The `{ modules, interfaces, defaultInterfaceOf }` view `classifyCallee` needs,
 *  read off the derived graph (empty-safe when the graph is absent). */
function calleeIndex(graph: DependencyGraph | null): Parameters<typeof classifyCallee>[1] {
  if (!graph) {
    return { modules: { has: () => false }, interfaces: { has: () => false } };
  }
  return {
    modules: graph.modulesByPath,
    interfaces: graph.interfacesByPath,
    defaultInterfaceOf: {
      get: (key: string): string | undefined => {
        const m = graph.modulesByPath.get(key);
        return m?.interfaces.find((i) => i.decl.unnamed && i.decl.hasBody)?.name;
      },
    },
  };
}

/** Depth-first collect every `simple` step, descending into all containers —
 *  the same traversal shape as hover's `collectCallers`. */
function collectSimpleSteps(steps: readonly ProcessStep[], out: SimpleStep[]): void {
  for (const step of steps) {
    switch (step.kind) {
      case "simple":
        out.push(step);
        break;
      case "each":
        collectSimpleSteps(step.steps, out);
        if (step.retry) {
          collectSimpleSteps(step.retry.attempt, out);
          for (const c of step.retry.catches) collectSimpleSteps(c.steps, out);
          if (step.retry.elseSteps) collectSimpleSteps(step.retry.elseSteps, out);
        }
        break;
      case "if":
        for (const b of step.branches) collectSimpleSteps(b.steps ?? [], out);
        if (step.elseSteps) collectSimpleSteps(step.elseSteps, out);
        break;
      case "select":
        for (const c of step.cases) collectSimpleSteps(c.steps, out);
        break;
      case "try":
        collectSimpleSteps(step.trySteps, out);
        for (const c of step.catches) collectSimpleSteps(c.steps, out);
        break;
      case "parallel":
        for (const b of step.branches) collectSimpleSteps(b.steps, out);
        if (step.fanOut) collectSimpleSteps(step.fanOut.steps, out);
        break;
      case "reversible":
        for (const item of step.items) {
          if (item.forward) {
            if (item.forward.kind === "block") collectSimpleSteps(item.forward.steps, out);
            else if (item.forward.kind === "simple") out.push(item.forward);
          }
          const a = item.unwind?.action;
          if (a) {
            if (a.kind === "block") collectSimpleSteps(a.steps, out);
            else if (a.kind === "simple") out.push(a);
          }
        }
        break;
      case "owner_block":
        collectSimpleSteps(step.steps, out);
        break;
      default:
        break; // do / fail / finish / go / note / prose / await — no simple callee
    }
  }
}

// =============================================================================
// HINT B — `count ( <edge-pattern> )` counts edges, not callers
// =============================================================================

/**
 * If `offset` lands on the `count` head of a `count ( <arrow> )` expression in a
 * view/policy/query/lens body in this file, return a hover explaining the
 * edge-vs-caller distinction — with live N/M numbers when the pattern is
 * evaluable against the current model, else the static distinction.
 */
export function countEdgeHover(workspace: Workspace, uri: string, offset: number): Hover | null {
  const decls = workspace.getFile(uri)?.file.declarations ?? [];
  let hit: ExprAggregate | null = null;
  for (const d of decls) {
    forEachAggregate(d, (agg) => {
      if (agg.op !== "count") return;
      if (agg.selector.kind !== "arrow") return; // only edge patterns
      const s = agg.source;
      if (!s) return;
      // Anchor on the `count` head only (up to the opening paren), so hovering an
      // operand inside still yields that element's own card.
      const headEnd = agg.selector.source?.start.offset ?? s.start.offset + "count".length;
      if (s.file && s.file !== uri) return;
      if (offset >= s.start.offset && offset < headEnd) hit = agg;
    });
  }
  if (!hit) return null;
  const agg: ExprAggregate = hit;

  const value = renderCountHint(agg, buildCountContext(workspace));
  const contents = { kind: "markdown" as const, value };
  return agg.source ? { contents, range: toLspRange(agg.source) } : { contents };
}

function buildCountContext(workspace: Workspace): EvalContext {
  const graph = workspace.dependencyGraph();
  const policies = new Map<string, PolicyDecl>();
  const queries = new Map<string, QueryDecl>();
  for (const u of workspace.uris()) {
    const pf = workspace.getFile(u)?.file;
    if (!pf) continue;
    for (const d of pf.declarations) {
      if (d.kind === "policy") policies.set(d.decl.name, d.decl);
      else if (d.kind === "query") queries.set(d.decl.name, d.decl);
    }
  }
  return {
    universe: buildNodeUniverse(workspace.cascadedModel()),
    graph: graph ?? undefined,
    closure: graph ? buildClosure(graph) : undefined,
    policies,
    queries,
  };
}

function renderCountHint(agg: ExprAggregate, ctx: EvalContext): string {
  const live = liveEdgeCounts(agg.selector, ctx);
  if (live) {
    const lines = [
      `**\`count (…)\` counts edges** — ${plural(live.edges, "edge")} from ` +
        `${plural(live.sources, "distinct source")}.`,
    ];
    if (live.edges !== live.sources) {
      lines.push(
        `For callers, count the sources: \`count (sources of (…))\` → ${live.sources}.`,
      );
    }
    return lines.join("\n\n");
  }
  // Static fallback — the distinction without live numbers.
  return [
    "**`count (…)` counts edges, not callers** — one edge per deriving step, so " +
      "two steps from the same source count twice.",
    "For distinct callers: `count (sources of (…))`.",
  ].join("\n\n");
}

/** Evaluate the arrow to its edge count + distinct-source count. `null` when the
 *  pattern is not live-evaluable in the editor context (e.g. needs a bound
 *  subject, or no graph) — the caller then shows the static distinction. */
function liveEdgeCounts(
  selector: Selector,
  ctx: EvalContext,
): { edges: number; sources: number } | null {
  if (!ctx.graph) return null;
  try {
    const res = evalSelector(selector, ctx);
    if (res.sort !== "edge") return null;
    const src = evalSelector({ kind: "extraction", head: "sources", inner: selector }, ctx);
    const sources = src.sort === "node" ? src.nodes.size : res.edges.size;
    return { edges: res.edges.size, sources };
  } catch {
    return null;
  }
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

// -----------------------------------------------------------------------------
// Aggregate discovery — walk a top-level declaration's selector/expression trees.
// -----------------------------------------------------------------------------

type Decl = NonNullable<ReturnType<Workspace["getFile"]>>["file"]["declarations"][number];

function forEachAggregate(d: Decl, visit: (a: ExprAggregate) => void): void {
  switch (d.kind) {
    case "view":
      for (const c of d.decl.clauses) walkClause(c, visit);
      break;
    case "policy":
      for (const r of d.decl.rules) walkRule(r, visit);
      break;
    case "query":
      walkSelector(d.decl.selector, visit);
      break;
    case "lens":
      for (const e of d.decl.entries) walkExpr(e.expr, visit);
      break;
    case "styleBundle":
      for (const m of d.decl.members) walkStyleMember(m, visit);
      break;
    default:
      break;
  }
}

function walkClause(c: ViewClause, visit: (a: ExprAggregate) => void): void {
  switch (c.kind) {
    case "show":
    case "hide":
    case "focus":
      walkSelector(c.selector, visit);
      break;
    case "style":
      walkSelector(c.subject, visit);
      for (const m of c.members) walkStyleMember(m, visit);
      break;
    case "lens":
      for (const e of c.entries) walkExpr(e.expr, visit);
      break;
    case "table":
      for (const col of c.columns) if (col.kind === "calc") walkExpr(col.expr, visit);
      break;
    default:
      break; // groupBy / on / flow / matrix / grid — no aggregate-bearing expr
  }
}

function walkRule(r: PolicyRule, visit: (a: ExprAggregate) => void): void {
  switch (r.kind) {
    case "forbid":
    case "except":
      walkSelector(r.selector, visit);
      break;
    case "require":
      walkSelector(r.subject, visit);
      walkSelector(r.obligation, visit);
      break;
    case "when":
      walkSelector(r.subject, visit);
      break;
    default:
      break; // escalate — no selector
  }
}

function walkStyleMember(m: StyleMember, visit: (a: ExprAggregate) => void): void {
  if (m.kind === "rewrite") walkExpr(m.value, visit);
  else if (m.kind === "group") for (const sub of m.members) walkStyleMember(sub, visit);
}

function walkSelector(s: Selector, visit: (a: ExprAggregate) => void): void {
  switch (s.kind) {
    case "where":
      walkExpr(s.expr, visit);
      break;
    case "arrow":
      walkSelector(s.head, visit);
      for (const h of s.hops) walkSelector(h.rhs, visit);
      break;
    case "extraction":
      walkSelector(s.inner, visit);
      break;
    case "in":
      walkSelector(s.arg, visit);
      break;
    case "and":
    case "or":
      for (const t of s.terms) walkSelector(t, visit);
      break;
    case "not":
      walkSelector(s.term, visit);
      break;
    default:
      break; // universal / this / outside / knob / ref / aspect / violating / on — leaves
  }
}

function walkExpr(e: Expr, visit: (a: ExprAggregate) => void): void {
  switch (e.kind) {
    case "aggregate":
      visit(e);
      walkSelector(e.selector, visit);
      break;
    case "exists":
      walkSelector(e.selector, visit);
      break;
    case "binary":
      walkExpr(e.left, visit);
      walkExpr(e.right, visit);
      break;
    case "unary":
      walkExpr(e.operand, visit);
      break;
    case "call":
      for (const a of e.args) walkExpr(a, visit);
      break;
    default:
      break; // literal / this / getter — no aggregate
  }
}

/** Does `span` (1-based line, offset-bearing) cover the file `uri` at `offset`? */
function spanCovers(span: SourceSpan, uri: string, offset: number): boolean {
  if (span.file && span.file !== uri) return false;
  return span.start.offset <= offset && offset <= span.end.offset;
}
